import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';

import { Nullable } from 'Shared/utils/types';
import { PaymentPlanType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { FlexiblePaymentFrequency } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { RateType } from 'QuoteApp/services/endpoints/GetRate';
import { getSelectedPaymentPlan } from 'QuoteApp/utils/paymentPlan';

import { PaymentOptionSummary } from './PlanComparison/types';
import {
  initialPriceModel,
  PriceContext,
  PriceEvents,
  PriceState,
  transition,
} from './domain';
import {
  mapPaymentPlanToOption,
  sanitizePaymentPlanOptions,
  sortPaymentPlanOptionsByFrequency,
} from './utils';

type PriceCustomizerContextType = {
  status: PriceState;
  frequency: Nullable<FlexiblePaymentFrequency>;
  dayOfMonth: PriceContext['dayOfMonth'];
  paymentPlanOptions: Array<PaymentOptionSummary>;
  selectedPaymentPlan: PaymentPlanType | null;
  jumpToState: (state: {
    status: PriceState;
    context: Partial<PriceContext>;
  }) => void;
  selectFrequency: (frequency: FlexiblePaymentFrequency) => void;
  selectDayOfWeek: (dayOfWeek: string) => void;
  selectBiweeklyStart: (biweeklyStart: string) => void;
  selectSemiMonthlyDates: (semiMonthly: string) => void;
  selectDayOfMonth: (dayOfMonth: string) => void;
};

type PriceCustomizerProviderProps = {
  children:
    | React.ReactNode
    | ((
        selectedFrequency: Nullable<FlexiblePaymentFrequency>
      ) => React.ReactNode);
  rate: RateType | null;
};

const PriceCustomizerContext = createContext<PriceCustomizerContextType>({
  status: 'idle',
  frequency: null,
  dayOfMonth: null,
  paymentPlanOptions: [],
  selectedPaymentPlan: null,
  jumpToState: () => {},
  selectFrequency: () => {},
  selectDayOfWeek: () => {},
  selectBiweeklyStart: () => {},
  selectSemiMonthlyDates: () => {},
  selectDayOfMonth: () => {},
});

export const PriceCustomizerProvider: FC<PriceCustomizerProviderProps> = ({
  children,
  rate,
}) => {
  const [priceModel, dispatch] = useReducer(transition, initialPriceModel);
  const { status, context } = priceModel;

  const selectedPaymentPlan = useMemo(
    () => (rate ? getSelectedPaymentPlan(rate) : null),
    [rate]
  );

  const jumpToState: PriceCustomizerContextType['jumpToState'] = (state) => {
    dispatch({
      type: PriceEvents.JUMP_TO_STATE,
      value: state,
    });
  };

  const selectFrequency = useCallback((frequency: FlexiblePaymentFrequency) => {
    dispatch({
      type: PriceEvents.SELECT_FREQUENCY,
      value: frequency,
    });
  }, []);

  const selectDayOfWeek = useCallback((dayOfWeek: string) => {
    dispatch({
      type: PriceEvents.SELECT_DAY_OF_WEEK,
      value: dayOfWeek,
    });
  }, []);

  const selectBiweeklyStart = useCallback((biweeklyStart: string) => {
    dispatch({
      type: PriceEvents.SELECT_BIWEEKLY_START_DATE,
      value: biweeklyStart,
    });
  }, []);

  const selectSemiMonthlyDates = useCallback((semiMonthly: string) => {
    dispatch({
      type: PriceEvents.SELECT_SEMI_MONTHLY_DATES,
      value: semiMonthly,
    });
  }, []);

  const selectDayOfMonth = useCallback((dayOfMonth: string) => {
    dispatch({
      type: PriceEvents.SELECT_DAY_OF_MONTH,
      value: dayOfMonth,
    });
  }, []);

  const paymentPlanOptions = useMemo(() => {
    if (!rate || !selectedPaymentPlan) return [];

    const paymentPlans = rate.paymentDetails.attributes.paymentPlans;
    const paymentOptions = paymentPlans.map(mapPaymentPlanToOption);

    const sanitizedPaymentOptions = sanitizePaymentPlanOptions(
      paymentOptions,
      selectedPaymentPlan
    );

    // Return options sorted by business-defined frequency order
    return sortPaymentPlanOptionsByFrequency(sanitizedPaymentOptions);
  }, [rate, selectedPaymentPlan]);

  if (!rate || !selectedPaymentPlan) return null;

  return (
    <PriceCustomizerContext.Provider
      value={{
        status,
        frequency: context.frequency,
        dayOfMonth: context.dayOfMonth,
        paymentPlanOptions,
        selectedPaymentPlan,
        jumpToState,
        selectFrequency,
        selectDayOfWeek,
        selectBiweeklyStart,
        selectSemiMonthlyDates,
        selectDayOfMonth,
      }}
    >
      {typeof children === 'function' ? children(context.frequency) : children}
    </PriceCustomizerContext.Provider>
  );
};

export const usePriceCustomizer = () => {
  const context = useContext(PriceCustomizerContext);

  if (!context) {
    throw new Error(
      'usePriceCustomizer must be used within a PriceCustomizerProvider'
    );
  }

  return context;
};
